import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { ActivityListItem } from './ActivityListItem';
import { HistoryListItem } from './HistoryListItem';
import { Payment } from '@lendica/utils';

type Variant = 'upcoming' | 'history';

interface VirtualizedPaymentsListProps {
    items: Payment[];
    variant: Variant;
    loadMore(): Promise<void> | void;
    hasNext: boolean;
    isFetchingMore?: boolean;
    itemSize?: number;
    onHistoryRowClick?(id: string): void;
}

const useMeasuredHeight = () => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = React.useState(0);

    React.useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const newHeight = Math.floor(entry.contentRect.height);
                setHeight(newHeight);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return { ref, height } as const;
};

export const VirtualizedPaymentsList: React.FC<VirtualizedPaymentsListProps> = props => {
    const {
        items,
        variant,
        loadMore,
        hasNext,
        isFetchingMore = false,
        itemSize,
        onHistoryRowClick,
    } = props;
    const { ref, height } = useMeasuredHeight();

    const itemCount = items.length + (hasNext ? 1 : 0);

    const Row = React.useCallback(
        ({ index, style }: ListChildComponentProps) => {
            if (index >= items.length) {
                return (
                    <Box style={style} display="flex" alignItems="center" justifyContent="center">
                        {hasNext ? <CircularProgress size={20} /> : null}
                    </Box>
                );
            }
            const p = items[index];
            if (variant === 'history') {
                return (
                    <div style={style}>
                        <HistoryListItem
                            key={p.id}
                            dealId={p.deal.substring(0, 8)}
                            date={p.event_date}
                            amount={p.check_amount}
                            transactionCategory={p.description_code}
                            paymentDirection={p.payment_direction}
                            status={p.resulting_status}
                            product={p.product_name ?? ''}
                            hasNavigation={Boolean(onHistoryRowClick)}
                            onNavigationClick={() => onHistoryRowClick?.(p.id)}
                        />
                    </div>
                );
            }
            return (
                <div style={style}>
                    <ActivityListItem
                        key={p.id}
                        dealId={p.deal.substring(0, 8)}
                        date={p.event_date}
                        amount={p.check_amount}
                        descriptionCode={p.description_code}
                        paymentDirection={p.payment_direction}
                        paymentMethod={p.payment_method}
                        paymentStatus={p.resulting_status}
                        product={p.product_name ?? ''}
                        hasNavigation={false}
                    />
                </div>
            );
        },
        [items, variant]
    );

    const handleItemsRendered = React.useCallback(
        ({ visibleStopIndex }: { visibleStopIndex: number }) => {
            const threshold = items.length - 3;
            if (hasNext && !isFetchingMore && visibleStopIndex >= threshold) {
                loadMore();
            }
        },
        [hasNext, isFetchingMore, items.length, loadMore]
    );

    return (
        <Box ref={ref} sx={{ height: '100%', overflow: 'hidden' }}>
            {height > 0 && (
                <FixedSizeList
                    height={height}
                    width={'100%'}
                    itemCount={itemCount}
                    itemSize={64}
                    onItemsRendered={info =>
                        handleItemsRendered({ visibleStopIndex: info.visibleStopIndex })
                    }
                    overscanCount={5}
                >
                    {Row}
                </FixedSizeList>
            )}
        </Box>
    );
};
